import Floatbar from "@app/components/Floatbar";
import { useSetToast } from "@app/components/Toast";
import { questionnaireLocaleAtom } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/ctx";
import { TagsMultiSelect } from "@app/shared/components/TagsMultiSelect";
import { SupportedLocales } from "@app/shared/utils/locales";
import {
    getSelectedIds,
    getSelectedRowsCount,
} from "@app/shared/utils/paginatedSelection";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { type PaginatedSelectedRows } from "@design-system/Table";
import { ResponseStatus, TagType } from "@generated/client/graphql";
import { useAtomValue } from "jotai";
import { useState, type FC } from "react";
import {
    useCreateSupplierTemplateQuestionGroup,
    useDeleteTemplateQuestions,
    useUpdateSupplierTemplateQuestions,
} from "../../data";
import { type QuestionMinimalListItem } from "../../types";
import { QuestionGroupSelect } from "../QuestionGroupSelect";
import { DeleteQuestionModal } from "./DeleteQuestionModal";
import { useSupplierQuestionsTableTranslations } from "./i18n";

const actions = {
    ASSIGN_QUESTIONNAIRE: "assign_questionnaire",
    ASSIGN_QUESTION_GROUP: "assign_question_group",
    DELETE_QUESTION: "delete_question",
} as const;

export const ActionBar: FC<{
    unset: () => void;
    paginatedSelectedRows: PaginatedSelectedRows;
    rowCount: number;
    questions: QuestionMinimalListItem[];
}> = ({ unset, paginatedSelectedRows, rowCount, questions }) => {
    const [activeAction, setActiveAction] = useState<string | undefined>(
        undefined,
    );

    const { updateQuestions } = useUpdateSupplierTemplateQuestions();

    const { createQuestionGroup } = useCreateSupplierTemplateQuestionGroup();

    const { deleteQuestions } = useDeleteTemplateQuestions();

    const { setToastSuccess, setToastError } = useSetToast();

    const currentLocale = useAtomValue(questionnaireLocaleAtom);

    const [selectedTagIds, setSelectedTagIds] = useState<(string | null)[]>([]);

    const [selectedQuestionGroupId, setSelectedQuestionGroupId] = useState<
        string | undefined
    >(undefined);

    const selectedRowsCount = getSelectedRowsCount(
        paginatedSelectedRows,
        rowCount,
    );

    const selectedQuestionIds = getSelectedIds(
        paginatedSelectedRows,
        questions.map((question) => question.id),
    );

    const isFloatbarOpen = !!selectedRowsCount;

    const onAssignQuestionnaire = () => {
        setActiveAction(actions.ASSIGN_QUESTIONNAIRE);
    };

    const onAssignQuestionGroup = () => {
        setActiveAction(actions.ASSIGN_QUESTION_GROUP);
    };

    const onDeleteQuestion = () => {
        setActiveAction(actions.DELETE_QUESTION);
    };

    const unsetActiveAction = () => {
        setActiveAction(undefined);
    };

    const handleAssignQuestionnaire = () => {
        updateQuestions(
            {
                ids: selectedQuestionIds,
                input: {
                    tagIds: selectedTagIds.filter(
                        (tagId) => typeof tagId === "string",
                    ),
                },
            },
            {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.updateSupplierTemplateQuestions?.status !==
                            ResponseStatus.Success
                    ) {
                        setToastError(
                            t(keys.toast_error_update_question, {
                                count: selectedQuestionIds.length,
                            }),
                        );
                        return;
                    }
                    setToastSuccess(
                        t(keys.toast_success_update_question, {
                            count: selectedQuestionIds.length,
                        }),
                    );
                },
            },
        );

        setSelectedTagIds([]);
        setActiveAction(undefined);
        unset();
    };

    const handleAssignQuestionGroup = () => {
        updateQuestions(
            {
                ids: selectedQuestionIds,
                input: {
                    questionGroupId: selectedQuestionGroupId || null,
                },
            },
            {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.updateSupplierTemplateQuestions?.status !==
                            ResponseStatus.Success
                    ) {
                        setToastError(
                            t(keys.toast_error_update_question, {
                                count: selectedQuestionIds.length,
                            }),
                        );
                        return;
                    }
                    setToastSuccess(
                        t(keys.toast_success_update_question, {
                            count: selectedQuestionIds.length,
                        }),
                    );
                },
            },
        );

        setSelectedQuestionGroupId(undefined);
        setActiveAction(undefined);
        unset();
    };

    const handleQuestionGroupCreate = ({
        englishName,
        frenchName,
    }: {
        englishName?: string;
        frenchName?: string;
    }) => {
        createQuestionGroup(
            {
                nameMultiLocale: {
                    en: englishName,
                    fr: frenchName,
                },
            },
            {
                onSettled(data) {
                    if (
                        data?.createQuestionGroup?.status ===
                            ResponseStatus.Success &&
                        data?.createQuestionGroup?.data?.id
                    ) {
                        setSelectedQuestionGroupId(
                            data?.createQuestionGroup?.data?.id,
                        );
                        return;
                    }
                    setToastError(
                        t(keys.modal_toast_error_create_question_group),
                    );
                },
            },
        );
    };

    const handleDeleteQuestion = () => {
        deleteQuestions(selectedQuestionIds, {
            onSettled(data, error) {
                if (
                    error ||
                    data?.deleteSupplierTemplateQuestions?.status !==
                        ResponseStatus.Success
                ) {
                    setToastError(
                        t(keys.toast_error_delete_question, {
                            count: selectedQuestionIds.length,
                        }),
                    );
                    return;
                }
                setToastSuccess(
                    t(keys.toast_success_delete_question, {
                        count: selectedQuestionIds.length,
                    }),
                );
            },
        });

        setActiveAction(undefined);
        unset();
    };

    const { t, keys } = useSupplierQuestionsTableTranslations();

    return (
        <>
            <Floatbar
                unset={unset}
                text={t(keys.action_bar_selected_questions, {
                    count: selectedRowsCount,
                })}
                open={isFloatbarOpen}
                primary={
                    <Dropdown>
                        <Dropdown.Trigger asChild>
                            <Button variant="outlined" size="sm">
                                {t(keys.action_bar_select_action)}
                                <Button.Icon name="chevronDown" size="sm" />
                            </Button>
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                            <Dropdown.Item onClick={onAssignQuestionnaire}>
                                <Dropdown.ItemIcon name="document" />
                                <Dropdown.ItemText
                                    variant="body-sm"
                                    className="text-primary"
                                >
                                    {t(keys.action_bar_assign_questionnaire)}
                                </Dropdown.ItemText>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={onAssignQuestionGroup}>
                                <Dropdown.ItemIcon name="tagEmptyCircle" />
                                <Dropdown.ItemText
                                    variant="body-sm"
                                    className="text-primary"
                                >
                                    {t(keys.action_bar_assign_question_group)}
                                </Dropdown.ItemText>
                            </Dropdown.Item>
                            <Dropdown.Item
                                variant="negative"
                                onClick={onDeleteQuestion}
                            >
                                <Dropdown.ItemIcon name="trash" />
                                <Dropdown.ItemText variant="body-sm">
                                    {t(keys.action_bar_delete_question)}
                                </Dropdown.ItemText>
                            </Dropdown.Item>
                        </Dropdown.Content>
                    </Dropdown>
                }
                actions={[
                    {
                        id: actions.ASSIGN_QUESTIONNAIRE,
                        selector: (
                            <TagsMultiSelect
                                tagsIds={selectedTagIds}
                                setTagsIds={setSelectedTagIds}
                                type={TagType.Questionnaire}
                                side="top"
                            />
                        ),
                        apply: (
                            <Button
                                size="sm"
                                onClick={handleAssignQuestionnaire}
                            >
                                {t(keys.apply_action)}
                            </Button>
                        ),
                        text: t(keys.action_bar_assign_questionnaire, {
                            count: selectedQuestionIds.length,
                        }),
                    },
                    {
                        id: actions.ASSIGN_QUESTION_GROUP,
                        selector: (
                            <QuestionGroupSelect
                                selectedGroupId={selectedQuestionGroupId}
                                onSelectedGroupIdChange={
                                    setSelectedQuestionGroupId
                                }
                                onQuestionGroupCreate={
                                    handleQuestionGroupCreate
                                }
                                locale={currentLocale ?? SupportedLocales.EN}
                            />
                        ),
                        apply: (
                            <Button
                                size="sm"
                                onClick={handleAssignQuestionGroup}
                            >
                                {t(keys.apply_action)}
                            </Button>
                        ),
                        text: t(keys.action_bar_assign_question_group, {
                            count: selectedQuestionIds.length,
                        }),
                    },
                ]}
                activeAction={activeAction}
                setActiveAction={setActiveAction}
            />
            <DeleteQuestionModal
                open={activeAction === actions.DELETE_QUESTION}
                onOpenChange={unsetActiveAction}
                onDelete={handleDeleteQuestion}
                questionCount={selectedRowsCount}
            />
        </>
    );
};
